/**
 * Completion Step (Step 4)
 * Shows successful completion of account setup/edit process
 */

import React from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  Security,
} from "@mui/icons-material";
import { Icon } from "../../../../components/ds";

const CompletionStep = ({
  accountData,
  selectedBroker,
  isEditMode,
  onComplete,
}) => {
  // What's available now features
  const availableFeatures = [
    {
      icon: <Icon name="gain" />,
      title: "Live Trading",
      description: "Place orders and manage positions in real-time",
    },
    {
      icon: <Icon name="bank" />,
      title: "Account Monitoring",
      description: "View balance, holdings, and transaction history",
    },
    {
      icon: <Security sx={{ color: "success.main" }} />,
      title: "Secure Connection",
      description: "Your account is securely connected and authenticated",
    },
    {
      icon: <Icon name="settings" />,
      title: "Account Management",
      description: "Update settings and manage your trading preferences",
    },
  ];

  return (
    <Box>
      <Typography variant="h6" gutterBottom color="text.primary">
        {isEditMode
          ? "Account Updated Successfully!"
          : "Account Setup Complete!"}
      </Typography>

      {/* Success Message */}
      <Box sx={{ textAlign: "center", mb: 4 }}>
        <Icon name="success" size={80} style={{ marginBottom: 16 }} />
        <Typography variant="h5" color="success.main" gutterBottom>
          All Done!
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {isEditMode
            ? "Your account has been successfully updated and validated."
            : "Your trading account has been successfully connected to DeeTrade."}
        </Typography>
      </Box>

      {/* Account Summary */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            {selectedBroker?.logo && (
              <img
                src={selectedBroker.logo}
                alt={selectedBroker.name}
                style={{ width: 40, height: 40 }}
              />
            )}
            <Box>
              <Typography variant="h6">{accountData?.account_name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedBroker?.name} • Connected & Validated
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Available Features */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>
            What you can do now:
          </Typography>
          <List>
            {availableFeatures.map((feature, index) => (
              <ListItem key={index}>
                <ListItemIcon>{feature.icon}</ListItemIcon>
                <ListItemText
                  primary={feature.title}
                  secondary={feature.description}
                />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>

      {/* Next Steps */}
      <Card sx={{ mb: 4, backgroundColor: "info.light" }}>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>
            Next Steps:
          </Typography>
          <Typography variant="body2">
            • Visit your account dashboard to monitor performance
            <br />
            • Set up trading strategies and preferences
            <br />
            • Start placing orders and managing your portfolio
            <br />• Explore advanced features and analytics
          </Typography>
        </CardContent>
      </Card>

      {/* Action Button */}
      <Box sx={{ textAlign: "center" }}>
        <button
          type="button"
          className="btn btn-primary btn-lg"
          onClick={onComplete}
        >
          Go to Trade Accounts
        </button>
      </Box>
    </Box>
  );
};

export default CompletionStep;
